<script>
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { selectOnFocus } from './../modules/helpers.js'

  const dispatch = createEventDispatcher();

  export let text = "Your Text Here";
  export let isPreview = false;

  let shiftDown = false
  let hovering = false
  let textareaEl
  let isMounted = false
  let internalText = text; // Track changes internally
  let isDirty = false; // Flag to indicate unsaved changes
  
  // Track component lifecycle
  onMount(() => {
    isMounted = true;
    internalText = text;
    if (textareaEl) {
      adjustTextareaHeight();
    }
  });

  onDestroy(() => {
    isMounted = false;
  });

  function adjustTextareaHeight() {
    if (!textareaEl) return;
    
    // Reset height to auto to get accurate scrollHeight
    textareaEl.style.height = 'auto';
    
    // Calculate the new height based on scrollHeight
    const newHeight = Math.max(textareaEl.scrollHeight, 4 * 10); // Min height 4rem (assuming 1rem = 16px)
    
    // Set the height
    textareaEl.style.height = `${newHeight}px`;
  }

  function handleInput(event) {
    if (!isMounted || !event.target) return;
    internalText = event.target.value || text;
    isDirty = internalText !== text;
    
    // Adjust height after content change
    adjustTextareaHeight();
  }

  function handleKeyup(event) {
    if (!isMounted) return;
    if (event.key === 'Shift') {
      shiftDown = false
    }
  }

  function handleKeyDown(event) {
    if (!isMounted) return;
    if (event.key === 'Shift') {
      shiftDown = true
    }
    if (event.key === 'Enter' && !shiftDown && internalText !== '') {
      updatePreviewText();
      enterText();
    }
  }

  function updatePreviewText() {
    if (isDirty) {
      dispatch('textChange', internalText);
      isDirty = false;
    }
  }

  function enterText() {
    if (isMounted && textareaEl) {
      textareaEl.blur()
    }
  }
  
  
</script>

<div class="StorefrontTextfield">
  <div class="textInput"
  on:mouseenter="{() => hovering = true}"
  on:mouseleave="{() => hovering = false}">
    <textarea 
      bind:this={textareaEl}
      on:input={handleInput}
      on:keyup|preventDefault={handleKeyup}
      on:keydown={handleKeyDown}
      use:selectOnFocus
      placeholder={text}
      autoComplete="off"
      spellcheck="false"
      rows="1"
      value={internalText}
      class:dirty={isDirty}
    ></textarea>
    <small class="instructions">press <span class="key">ENTER</span> to update previews and <span class="key">SHIFT + ENTER</span> for new line</small>
    {#if isPreview && isMounted}
      <svg><use class="svg-icon" xlink:href="#icon-edit"/></svg>
    {/if}
    {#if isDirty}
      <button class="update-button" on:click={updatePreviewText}>&rarr;</button>
    {/if}
  </div>
</div>

<style>
  .StorefrontTextfield {
    z-index: 1;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .textInput {
    position: relative;
    width: fit-content;
    gap: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .textInput textarea {
    resize: none;
    width: 46rem;
    min-height: 4rem;
    padding: 0.4rem 1.4rem;
    white-space: pre-wrap;
    text-align: center;
    font-family: var(--PensumSans-Bold);
    font-size: 2.1rem;
    font-weight: 600;
    line-height: 2.4rem;
    color: var(--c-textfield-text);
    background-color: var(--c-textfield-bg);
    border: 2px solid var(--c-pill-bg);
    border-radius: 2rem;
    transition-property: border-color, height;
    transition-duration: var(--transition-duration);
    transition-timing-function: var(--transition-function);
    overflow: hidden;
  }

  .textInput textarea.dirty {
    border-color: var(--c-accent-orange);
  }

  .textInput textarea::placeholder {
    color: var(--c-text-faded);
  }

  .textInput:hover textarea:not(:focus) {
    border-color: var(--c-pill-bg-hover);
  }

  .textInput:hover svg {
    stroke: var(--c-accent) !important;
  }

  .textInput textarea:focus {
    border-color: var(--c-accent);
  }

  .instructions {
    opacity: 0;
    transition: opacity var(--transition-props);
  }

  .textInput:focus-within .instructions {
    opacity: 1;
  }

  .instructions .key {
    font-family: var(--PensumSans-Bold);
    font-size: 1.3rem;
  }

  .update-button {
    position: absolute;
    right: 0.5rem;
    bottom: 2.8rem;
    border: none;
    width: 4rem;
    height: 3rem;
    border-radius: 1.5rem;
    font-family: var(--PensumSans-Bold);
    font-size: 1.8rem;
    background-color: var(--c-accent);
    color: var(--c-text-on-fill);
    cursor: pointer;
    transition: opacity var(--transition-props);
  }

  .update-button:hover {
    color: var(--c-text-on-fill-hover);
  }

  @media only screen and (max-width: 870px) {
    .textInput textarea {
      width: 100%;
    }
  }

  svg {
    pointer-events: none;
    position: absolute;
    top: 0.3rem;
    right: 0.2rem;
    width: 3.6rem;
    height: 3.6rem;
    stroke: var(--c-textfield-text);
    transition: stroke var(--transition-props);
  }

</style>
